// Executive logs — Luigi's booking, service, dispatch, GiftPass and wallet
// histories (Leon, 2026-10-16), one tab per log. Only a Superadmin reaches
// this page (permissions.ts), and the admin function re-checks the role and
// writes one audit row each time a log is loaded, before any row leaves it.
// Each tab is a spreadsheet (components/sheet, Leon 2026-10-23, mockup v13):
// click rows to select them, Copy into Google Sheets with headers, one total
// per money column, filter menus that fit each column (the date one opens on
// this month), group by and saved views. Everything works on the rows already
// loaded; "Export N rows" saves the shown rows in Luigi's column order.

import { Button } from "@bednbody/ui/components/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { SheetTable, type SheetTableColumn } from "@/components/sheet/SheetTable";
import { callEdgeFunction, EdgeFunctionError } from "@/lib/edge-functions";
import { EXEC_LOG_COLUMNS, EXEC_LOG_TITLES, type ExecLogName } from "./execLogColumns";
import type { LogoMark, PillTone } from "@/components/sheet/SheetGrid";
import { displayValue, EXEC_LOG_VIEWS } from "./execLogViews";
import { type PartnerCodes, readableRows } from "./execLogIds";
import { toCsv } from "./toCsv";
import { supabase } from "@/lib/supabase";
import { CodeDialog } from "./ExecLogsCode";

interface ExecLogExport {
  log: ExecLogName;
  rowCount: number;
  rows: Record<string, unknown>[];
}

type LogRow = Record<string, unknown> & { __row: number };

// Plain text tabs: an icon on every log was noise (Leon, 2026-10-26).
// The order of Luigi's workbook; Partner balances and Rider session pay are
// ours (not in his workbook), so they come last.
const LOGS: ExecLogName[] = [
  "bookings",
  "services",
  "dispatches",
  "giftpasses",
  "giftpass_orders",
  "client_wallet",
  "partner_transactions",
  "partner_wallet",
  "rider_sessions",
  "earnings_daily",
  "wallet_daily",
];

/** Every column in Luigi's order, or — for a selection — only the selected ones, in sheet order. */
function downloadCsv(
  log: ExecLogName,
  rows: Record<string, unknown>[],
  keys: string[] | null,
  codes: PartnerCodes,
) {
  const all = EXEC_LOG_COLUMNS[log];
  const columns = keys ? keys.flatMap((key) => all.filter((column) => column.key === key)) : all;
  // The file shows the same references as the screen (execLogIds.ts).
  const csv = toCsv(columns, readableRows(rows, codes));
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `bednbody-${log}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** Money columns are shown and summed in pesos. */
const MONEY_KEY =
  /price|surcharge|_used|discount|total|processing|fee|paid|amount|share|_php|balance|earnings|held|pay$|service_online|service_cash|cash_received|cash_movement|withdraw/;
// Not "discount" — that is money. Whole words only.
const NOT_MONEY = /(^|_)km($|_)|(^|_)count($|_)|minutes|(^|_)passes($|_)/;
/** Refs, IDs and dates read best in monospace. */
const MONO_KEY = /ref|_id$|date|timestamp|_at$|_on$/;
/** Full date-times, stored in UTC, read in Philippine time. */
const TIME_KEY = /timestamp|_at$/;
const TEXT_KEY = /^(card_count|generation_batch|year_generated|date_generated)$/;
// Bumped when the default columns or filters change, so a view a browser saved
// on the old setup does not keep it (2026-10-30: Luigi's format, compact).
const SCOPE_VERSION = "v3";
/** Filtered by Today / This week / This month or a range. */
const DATE_KEY = /date|timestamp|_at$|_on$/;
// Style F (Leon, 2026-10-26, mockup board 3): status as a coloured word,
// payment with a small logo tile. Everything else stays plain text — colour
// only where it means something. The tiles are our own marks for now; the
// official Maya, QR Ph and card logos replace them when we have the files.
function statusTone(text: string): PillTone {
  if (/completed|redeemed|claimed|active|paid|^used$|^received$|^delivered$/i.test(text))
    return "success";
  if (/cancel|expired|invalid|void/i.test(text)) return "muted";
  if (/refund|pending|awaiting|waiting|needs/i.test(text)) return "warning";
  return "info";
}
function paymentLogo(text: string): LogoMark {
  if (/giftpass/i.test(text))
    return { mark: "GP", className: "bg-primary text-primary-foreground" };
  if (/cash/i.test(text)) return { mark: "₱", className: "bg-success text-primary-foreground" };
  if (/wallet|credit/i.test(text)) return { mark: "W", className: "bg-primary/10 text-primary" };
  if (/maya/i.test(text) && !/qr/i.test(text))
    return { mark: "maya", className: "bg-foreground text-success" };
  if (/card|visa|master/i.test(text))
    return { mark: "VISA", className: "bg-info text-primary-foreground" };
  return { mark: "QR", className: "bg-card text-info ring-1 ring-border" };
}
const TONES: Record<string, (text: string) => PillTone> = {
  status: statusTone,
  payment_verification: statusTone,
  delivery_status: statusTone,
};
const LOGOS: Record<string, (text: string) => LogoMark> = { payment_method: paymentLogo };

/** On screen only: a long ID reads "1bf0…6446", a time "Oct 21 · 10:17 PM". Copy keeps the full text. */
function cellText(key: string): ((text: string) => string) | undefined {
  if (key.endsWith("_id"))
    return (text) => (text.length > 14 ? `${text.slice(0, 4)}…${text.slice(-4)}` : text);
  if (TIME_KEY.test(key)) return (text) => text.replace(/, \d{4}, /, " · ");
  return undefined;
}

const NOUNS: Record<ExecLogName, readonly [string, string]> = {
  bookings: ["booking", "bookings"],
  services: ["service", "services"],
  dispatches: ["dispatch", "dispatches"],
  giftpasses: ["GiftPass", "GiftPasses"],
  giftpass_orders: ["order", "orders"],
  client_wallet: ["entry", "entries"],
  partner_wallet: ["staff member", "staff"],
  rider_sessions: ["session", "sessions"],
  partner_transactions: ["transaction", "transactions"],
  earnings_daily: ["day", "days"],
  wallet_daily: ["day", "days"],
};

const pesoAmount = new Intl.NumberFormat("en-PH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const manilaTime = new Intl.DateTimeFormat("en-PH", {
  timeZone: "Asia/Manila",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** What a cell reads as: 1,099.00 for money, "Oct 21, 2026, 10:17 PM" for a time, a label for a code. */
function readable(log: ExecLogName, key: string, value: string): string {
  if (MONEY_KEY.test(key) && !NOT_MONEY.test(key)) {
    const number = Number(value);
    return Number.isFinite(number) ? pesoAmount.format(number) : value;
  }
  if (TIME_KEY.test(key)) {
    const time = new Date(value);
    return Number.isNaN(time.getTime()) ? value : manilaTime.format(time);
  }
  return displayValue(log, key, value);
}

const BALANCE_KEYS = [
  "posted_earnings",
  "cash_held",
  "payout_held",
  "cash_retained",
  "payout_paid",
  "available_balance",
] as const;
/** A partner whose every balance figure is zero. */
function isZeroBalance(row: Record<string, unknown>): boolean {
  return BALANCE_KEYS.every((key) => Number(row[key] ?? 0) === 0);
}

/** Each partner's code (MT-… / MR-…) by their internal ID, to show the code everywhere. */
function usePartnerCodes(): PartnerCodes {
  const query = useQuery({
    queryKey: ["exec-logs", "partner-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, role, therapist_code, rider_code")
        .in("role", ["therapist", "rider"]);
      if (error) throw error;
      return new Map(
        (data ?? []).flatMap((s) => {
          const code = s.role === "rider" ? s.rider_code : s.therapist_code;
          return code ? [[s.id, code] as const] : [];
        }),
      );
    },
    staleTime: 5 * 60_000,
  });
  return query.data ?? EMPTY_CODES;
}
const EMPTY_CODES: PartnerCodes = new Map();

function LogTab({
  log,
  onExport,
}: {
  log: ExecLogName;
  onExport: (rows: LogRow[], keys: string[] | null) => void;
}) {
  const dateKey = EXEC_LOG_VIEWS[log].dateKey;
  const codes = usePartnerCodes();

  const query = useQuery({
    queryKey: ["exec-logs", log],
    // Viewing needs no authenticator code (Leon, 2026-10-29); Export does.
    queryFn: () => callEdgeFunction<ExecLogExport>(`admin/exec-logs/${log}/view`),
    // Each load is audited, so a tab is read once per visit, not on every focus.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // References made readable once, here: the grid, filters, copy and the
  // pop-up all see the same values as the export (execLogIds.ts).
  const everyRow = useMemo<LogRow[]>(
    () =>
      readableRows(query.data?.rows ?? [], codes).map((row, index) => ({ ...row, __row: index })),
    [query.data, codes],
  );
  // Partner balances lists every partner; most hold nothing. Show those with
  // money first, the rest one click away (Leon, 2026-10-30). Export is whole.
  const [showZero, setShowZero] = useState(false);
  const zeroCount = useMemo(
    () => (log === "partner_wallet" ? everyRow.filter(isZeroBalance).length : 0),
    [log, everyRow],
  );
  const allRows = useMemo(
    () => (zeroCount > 0 && !showZero ? everyRow.filter((row) => !isZeroBalance(row)) : everyRow),
    [everyRow, zeroCount, showZero],
  );

  const columns = useMemo<SheetTableColumn[]>(
    () =>
      EXEC_LOG_COLUMNS[log].map((column) => ({
        key: column.key,
        label: column.label,
        labelOf: (value: string) => readable(log, column.key, value),
        // A client ID is a long code: narrow, the whole of it on hover and in a copy.
        width: column.key === "client_id" ? 140 : TIME_KEY.test(column.key) ? 190 : undefined,
        money: MONEY_KEY.test(column.key) && !NOT_MONEY.test(column.key),
        mono: MONO_KEY.test(column.key),
        // A card's count, batch and year are labels: never totalled.
        text: TEXT_KEY.test(column.key),
        cell: cellText(column.key),
        emphasizeTail: column.key.endsWith("_ref"),
        // An order reference opens that order, where the buyer details are.
        href:
          column.key === "order_ref"
            ? (text: string) => `/giftpass?order=${encodeURIComponent(text)}`
            : undefined,
        date: DATE_KEY.test(column.key),
        tone: TONES[column.key],
        logo: LOGOS[column.key],
      })),
    [log],
  );

  if (query.isError) {
    return (
      <div className="space-y-3">
        <p role="alert" className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : "The log failed to load."}
        </p>
        <Button type="button" variant="outline" onClick={() => query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <SheetTable
      extra={
        zeroCount > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowZero((v) => !v)}>
            {showZero ? "Hide partners with ₱0" : `Show ${zeroCount} partners with ₱0`}
          </Button>
        ) : null
      }
      columns={columns}
      emptyDescription={
        allRows.length === 0
          ? "Rows appear here as soon as there is activity."
          : "Try a wider date range in the date column, or clear a filter."
      }
      emptyTitle={
        allRows.length === 0 ? `No ${EXEC_LOG_TITLES[log].toLowerCase()} yet` : "No rows match"
      }
      // A first visit opens on this month (Leon, 2026-10-19); the date column's menu widens it.
      defaultView={{
        ...(dateKey
          ? { conditions: { [dateKey]: { op: "date", preset: "month", from: "", to: "" } } }
          : {}),
        hiddenColumns: EXEC_LOG_VIEWS[log].hiddenByDefault ?? [],
        hidden: EXEC_LOG_VIEWS[log].hiddenValuesByDefault ?? {},
      }}
      // Compact rows and columns: read like his workbook (Leon, 2026-10-30).
      dense
      idOf={(row) => String(row.__row)}
      loading={query.isPending}
      onExport={onExport}
      rowNoun={NOUNS[log]}
      summaryKeys={EXEC_LOG_VIEWS[log].totals
        .filter((total) => !total.countWhere)
        .map((total) => total.key)}
      rows={allRows}
      scope={`exec-logs.${log}.${SCOPE_VERSION}`}
    />
  );
}

interface PendingExport {
  log: ExecLogName;
  rows: LogRow[];
  keys: string[] | null;
}

export function ExecLogsPage() {
  const [active, setActive] = useState<ExecLogName>("bookings");
  const [pending, setPending] = useState<PendingExport | null>(null);
  const [exporting, setExporting] = useState(false);
  const codes = usePartnerCodes();

  // Every export asks for the code (Leon, 2026-10-29). The file is built from
  // what the export route returns — which the server sends only with a fresh
  // code — keeping the rows that were picked on screen.
  async function exportAfterCode() {
    if (!pending) return;
    setExporting(true);
    try {
      const fresh = await callEdgeFunction<ExecLogExport>(`admin/exec-logs/${pending.log}/export`);
      const picked = new Set(pending.rows.map((row) => String(row.row_key)));
      const rows = fresh.rows.filter((row) => picked.has(String(row.row_key)));
      downloadCsv(pending.log, rows, pending.keys, codes);
      setPending(null);
    } catch (error) {
      toast.error("Couldn't export", {
        description:
          error instanceof EdgeFunctionError && error.code === "STEP_UP_REQUIRED"
            ? "The code expired. Try Export again."
            : error instanceof Error
              ? error.message
              : undefined,
      });
      setPending(null);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-5 p-4">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Executive logs</h1>
        <p className="text-sm text-muted-foreground">
          Accounting histories · client ID only · Philippine time · Super Admin only, every view is
          recorded · Export asks for your authenticator code
        </p>
      </header>

      <Tabs value={active} onValueChange={(value) => setActive(value as ExecLogName)}>
        {/* Slim text tabs (mockups v13–v15): the grid gets the screen. */}
        <TabsList
          className="w-full justify-start overflow-x-auto border-b border-border"
          variant="line"
        >
          {LOGS.map((log) => (
            <TabsTrigger className="flex-none px-3" key={log} value={log}>
              {EXEC_LOG_TITLES[log]}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* Only the open tab mounts, so only the log looked at is loaded and audited. */}
        <TabsContent value={active} className="mt-4">
          <LogTab
            key={active}
            log={active}
            onExport={(rows, keys) => setPending({ log: active, rows, keys })}
          />
        </TabsContent>
      </Tabs>
      <CodeDialog
        busy={exporting}
        onCancel={() => setPending(null)}
        onVerified={() => void exportAfterCode()}
        open={pending !== null}
      />
    </div>
  );
}
